<template>
  <v-dialog
    :value="value"
    max-width="900px"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">
          {{ headerTitle }}
        </span>
        <v-btn
          icon
          outlined
          color="error"
          small
          :disabled="uploadingImage"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-row dense>
            <v-col cols="12">
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.nameLabel") }}<span class="required-mark">*</span>
              </div>
              <v-text-field
                v-model="form.name"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.namePlaceholder')"
                :rules="nameRules"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.descriptionLabel") }}
              </div>
              <v-textarea
                v-model="form.description"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.descriptionPlaceholder')"
                :rules="descriptionRules"
                outlined
                dense
                rows="4"
                auto-grow
                class="bio-field"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.websiteLabel") }}
              </div>
              <v-text-field
                v-model="form.website"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.websitePlaceholder')"
                :rules="urlRules"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.youtubeLabel") }}
              </div>
              <v-text-field
                v-model="form.youtube"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.youtubePlaceholder')"
                :rules="handleRulesFor('youtube')"
                outlined
                dense
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.facebookLabel") }}
              </div>
              <v-text-field
                v-model="form.facebook"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.facebookPlaceholder')"
                :rules="handleRulesFor('facebook')"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.instagramLabel") }}
              </div>
              <v-text-field
                v-model="form.instagram"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.instagramPlaceholder')"
                :rules="handleRulesFor('instagram')"
                outlined
                dense
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.tiktokLabel") }}
              </div>
              <v-text-field
                v-model="form.tiktok"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.tiktokPlaceholder')"
                :rules="handleRulesFor('tiktok')"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div class="field-label">
                {{ $t("admin.productsDatabase.publishersDatabase.xLabel") }}
              </div>
              <v-text-field
                v-model="form.x"
                :placeholder="$t('admin.productsDatabase.publishersDatabase.xPlaceholder')"
                :rules="handleRulesFor('twitter')"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <div class="field-label mt-4">
            {{ $t("admin.productsDatabase.publishersDatabase.attachImage") }}
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/png"
            class="d-none"
            @change="onFileSelected"
          >
          <v-sheet
            outlined
            rounded
            class="pa-4 d-flex align-center"
            min-height="120"
          >
            <div class="preview-box mr-4">
              <img
                v-if="imagePreviewUrl"
                :src="imagePreviewUrl"
                class="preview-img"
                alt=""
              >
              <v-icon
                v-else
                size="48"
                color="grey lighten-1"
              >
                mdi-image-outline
              </v-icon>
            </div>
            <div class="d-flex flex-column">
              <v-btn
                text
                color="primary"
                @click="triggerFilePicker"
              >
                {{ $t("admin.productsDatabase.publishersDatabase.uploadImageOrLogo") }}
              </v-btn>
              <span
                v-if="form.profileImageFile"
                class="text-caption mt-1"
              >
                {{ form.profileImageFile.name }}
                <v-btn
                  icon
                  x-small
                  :title="$t('admin.productsDatabase.publishersDatabase.removeImage')"
                  @click="clearFile"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </span>
              <span
                v-if="imageError"
                class="text-caption error--text mt-1"
              >
                {{ imageError }}
              </span>
              <v-progress-linear
                v-if="uploadingImage"
                :value="uploadProgress"
                color="primary"
                height="6"
                class="mt-2"
              />
            </div>
          </v-sheet>
        </v-form>
      </v-card-text>

      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn
          color="grey darken-3"
          dark
          :disabled="uploadingImage"
          @click="clear"
        >
          {{ $t("admin.productsDatabase.publishersDatabase.clear") }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="uploadingImage"
          :disabled="uploadingImage"
          @click="onSave"
        >
          {{ $t("admin.productsDatabase.publishersDatabase.savePublisherInformation") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { extractSocialHandle } from "@/helpers/socialHandleHelper";

const HANDLE_REGEX = /^@?[A-Za-z0-9._-]+$/;
const URL_REGEX = /^https?:\/\/.+\..+/;

export default {
  name: "PublisherProfileDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    publisher: {
      type: Object,
      default: null,
    },
    uploadingImage: {
      type: Boolean,
      default: false,
    },
    uploadProgress: {
      type: Number,
      default: 0,
    },
  },

  emits: ["input", "save"],

  data() {
    return {
      valid: false,
      form: this.emptyForm(),
      imageError: "",
      fileBlobUrl: null,
    };
  },

  computed: {
    mode() {
      return this.publisher ? "edit" : "create";
    },
    headerTitle() {
      return this.mode === "edit"
        ? this.$t("admin.productsDatabase.publishersDatabase.editPublisherTitle")
        : this.$t("admin.productsDatabase.publishersDatabase.addPublisherTitle");
    },
    imagePreviewUrl() {
      if (this.fileBlobUrl) return this.fileBlobUrl;
      if (this.form.imageUrl) return this.form.imageUrl;
      return null;
    },
    nameRules() {
      return [
        (v) =>
          !!(v && v.trim()) ||
          this.$t("admin.productsDatabase.publishersDatabase.nameRequired"),
        (v) =>
          (v && v.trim().length >= 2) ||
          this.$t("admin.productsDatabase.publishersDatabase.nameMinLength"),
      ];
    },
    descriptionRules() {
      return [
        (v) =>
          !(v && v.trim()) ||
          v.trim().length >= 10 ||
          this.$t("admin.productsDatabase.publishersDatabase.descriptionMinLength"),
      ];
    },
    urlRules() {
      return [
        (v) =>
          !v ||
          !v.trim() ||
          URL_REGEX.test(v.trim()) ||
          this.$t("admin.productsDatabase.publishersDatabase.invalidUrl"),
      ];
    },
  },

  watch: {
    value(open) {
      if (!open) {
        this.revokeBlob();
        return;
      }
      this.form = this.publisher
        ? this.fromPublisher(this.publisher)
        : this.emptyForm();
      this.imageError = "";
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
  },

  beforeDestroy() {
    this.revokeBlob();
  },

  methods: {
    emptyForm() {
      return {
        name: "",
        description: "",
        website: "",
        youtube: "",
        facebook: "",
        instagram: "",
        tiktok: "",
        x: "",
        imageUrl: "",
        profileImageFile: null,
      };
    },
    fromPublisher(publisher) {
      return {
        name: publisher.name || "",
        description: publisher.description || "",
        website: publisher.website || "",
        youtube: publisher.youtube || "",
        facebook: publisher.facebook || "",
        instagram: publisher.instagram || "",
        tiktok: publisher.tiktok || "",
        x: publisher.x || "",
        imageUrl: publisher.imageUrl || "",
        profileImageFile: null,
      };
    },
    clear() {
      this.form = this.emptyForm();
      this.revokeBlob();
      this.imageError = "";
      this.$refs.form?.resetValidation();
    },
    close() {
      this.$emit("input", false);
    },
    handleRulesFor(platform) {
      return [
        (v) => {
          if (!v || !v.trim()) return true;
          const handle = extractSocialHandle(platform, v);
          if (!handle || !HANDLE_REGEX.test(handle)) {
            return this.$t(
              "admin.productsDatabase.publishersDatabase.invalidHandle"
            );
          }
          return true;
        },
      ];
    },
    onSave() {
      if (!this.$refs.form.validate()) return;
      const trim = (v) => (v || "").trim();
      const payload = {
        name: trim(this.form.name),
        description: trim(this.form.description) || null,
        website: trim(this.form.website),
        youtube: extractSocialHandle("youtube", this.form.youtube) || "",
        facebook: extractSocialHandle("facebook", this.form.facebook) || "",
        instagram: extractSocialHandle("instagram", this.form.instagram) || "",
        tiktok: extractSocialHandle("tiktok", this.form.tiktok) || "",
        x: extractSocialHandle("twitter", this.form.x) || "",
        profileImageFile: this.form.profileImageFile,
      };
      this.$emit("save", { payload, mode: this.mode });
    },
    triggerFilePicker() {
      this.imageError = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 2 * 1024 * 1024) {
        this.clearFile();
        this.imageError = this.$t(
          "admin.productsDatabase.publishersDatabase.imageTooLarge"
        );
        return;
      }
      const allowed = ["image/jpeg", "image/png"];
      if (!allowed.includes(file.type)) {
        this.clearFile();
        this.imageError = this.$t(
          "admin.productsDatabase.publishersDatabase.imageInvalidType"
        );
        return;
      }

      this.revokeBlob();
      this.fileBlobUrl = URL.createObjectURL(file);
      this.form.profileImageFile = file;
      this.imageError = "";
    },

    clearFile() {
      this.revokeBlob();
      this.form.profileImageFile = null;
      this.imageError = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    revokeBlob() {
      if (this.fileBlobUrl) {
        URL.revokeObjectURL(this.fileBlobUrl);
        this.fileBlobUrl = null;
      }
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 4px;
}
.required-mark {
  color: var(--v-error-base);
  margin-left: 2px;
}
::v-deep input::placeholder {
  font-size: 12px;
}
::v-deep .v-textarea.bio-field textarea {
  max-height: 300px;
}
.preview-box {
  width: 96px;
  height: 96px;
  border: 1px solid var(--v-lightAccent-base, #e0e0e0);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  flex-shrink: 0;
  overflow: hidden;
}
.preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
</style>
